import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
import { cartSounds } from "@/lib/cartSounds";

export interface CartItem {
  id: string;
  restaurant_id: string;
  restaurant_name: string;
  name: string;
  price: number;
  quantity: number;
  image_url?: string;
}

interface CartContextType {
  items: CartItem[];
  restaurantId: string | null;
  restaurantName: string | null;
  addToCart: (item: Omit<CartItem, "quantity">) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  getTotalPrice: () => number;
  getTotalItems: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState<string | null>(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      const parsed = JSON.parse(savedCart);
      setItems(parsed.items || []);
      setRestaurantId(parsed.restaurantId || null);
      setRestaurantName(parsed.restaurantName || null);
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify({ items, restaurantId, restaurantName }));
  }, [items, restaurantId, restaurantName]);

  const triggerHapticFeedback = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  const playAddSound = () => {
    try {
      cartSounds.playAddToCart();
    } catch (e) {
      // Silently fail
    }
  };

  const addToCart = (item: Omit<CartItem, "quantity">) => {
    // Check if adding from different restaurant
    if (restaurantId && restaurantId !== item.restaurant_id) {
      toast.error("Вы можете заказать только из одного ресторана за раз");
      return;
    }

    triggerHapticFeedback();
    playAddSound();

    // Check if item already in cart
    const existingItem = items.find((i) => i.id === item.id);
    if (existingItem) {
      updateQuantity(item.id, existingItem.quantity + 1);
      toast.success("Количество увеличено");
    } else {
      setItems([...items, { ...item, quantity: 1 }]);
      setRestaurantId(item.restaurant_id);
      setRestaurantName(item.restaurant_name);
      toast.success("Добавлено в корзину");
    }
  };

  const removeFromCart = (itemId: string) => {
    const newItems = items.filter((item) => item.id !== itemId);
    setItems(newItems);
    if (newItems.length === 0) {
      setRestaurantId(null);
      setRestaurantName(null);
    }
    toast.success("Удалено из корзины");
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setItems(items.map((item) => (item.id === itemId ? { ...item, quantity } : item)));
  };

  const clearCart = () => {
    setItems([]);
    setRestaurantId(null);
    setRestaurantName(null);
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        items,
        restaurantId,
        restaurantName,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalPrice,
        getTotalItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};
